// pages/signature/modals/tabs/CardSigner.tsx
import React from "react";
import { Avatar, Box, Chip, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ReplayIcon from "@mui/icons-material/Replay";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import { alpha, useTheme } from "@mui/material/styles";

export type CardSignerStatus = "waiting" | "signed" | "rejected" | string;

/** tipos suportados (somente estes 3) */
export type ParticipantType = "signer" | "witness" | "observers";

/** helpers para listas/predicados */
export const isSignerType = (t: ParticipantType) => t === "signer" || t === "witness";
export const isObserverType = (t: ParticipantType) => t === "observers";

export interface CardSignerProps {
	name: string;
	email: string;
	documentMasked: string;

	/** Tipo técnico vindo do backend (estritamente 3 tipos) */
	type: ParticipantType;

	/** Status técnico (traduzido fora, se quiser) */
	status?: CardSignerStatus;

	onResendLink?: () => void;
	onRemove?: () => void;

	dense?: boolean;

	canResend?: boolean; // default true
	canRemove?: boolean; // default true
	menuDisabled?: boolean; // força desabilitar todo o menu
}

type VisualResolved = {
	chipBg: string;
	chipColor: string;
	cardBg: string;
	cardBorder: string;
	accent: string;
	ChipIcon: React.ElementType;
	chipLabel: string;
	chipSx: object;
};

const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();

function resolveVisualForWhoSigns(theme: any, status?: CardSignerStatus): VisualResolved {
	const s = norm(status);
	if (s === "signed") {
		return {
			chipBg: alpha(theme.palette.success.main, 0.15),
			chipColor: theme.palette.success.main,
			cardBg: alpha(theme.palette.success.main, 0.08),
			cardBorder: alpha(theme.palette.success.main, 0.2),
			accent: theme.palette.success.main,
			ChipIcon: CheckCircleRoundedIcon,
			chipLabel: "Assinado",
			chipSx: {
				bgcolor: alpha(theme.palette.success.main, 0.15),
				color: theme.palette.success.main,
				border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
			},
		};
	}
	if (s === "rejected") {
		return {
			chipBg: alpha(theme.palette.error.main, 0.1),
			chipColor: theme.palette.error.main,
			cardBg: alpha(theme.palette.error.main, 0.06),
			cardBorder: alpha(theme.palette.error.main, 0.2),
			accent: theme.palette.error.main,
			ChipIcon: ErrorRoundedIcon,
			chipLabel: "Recusado",
			chipSx: {
				bgcolor: alpha(theme.palette.error.main, 0.1),
				color: theme.palette.error.main,
				border: `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
			},
		};
	}
	// waiting / default
	return {
		chipBg: alpha(theme.palette.warning.main, 0.12),
		chipColor: theme.palette.warning.main,
		cardBg: alpha(theme.palette.warning.main, 0.06),
		cardBorder: alpha(theme.palette.warning.main, 0.2),
		accent: theme.palette.warning.main,
		ChipIcon: ScheduleRoundedIcon,
		chipLabel: "Pendente",
		chipSx: {
			bgcolor: alpha(theme.palette.warning.main, 0.12),
			color: theme.palette.warning.main,
			border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
		},
	};
}

// visual neutro para quem NÃO assina (observadores)
function resolveNeutral(theme: any): VisualResolved {
	return {
		chipBg: alpha(theme.palette.grey[500], 0.12),
		chipColor: theme.palette.text.primary,
		cardBg: theme.palette.background.paper,
		cardBorder: theme.palette.divider,
		accent: theme.palette.divider,
		ChipIcon: ScheduleRoundedIcon,
		chipLabel: "",
		chipSx: {
			bgcolor: alpha(theme.palette.grey[500], 0.12),
			color: theme.palette.text.secondary,
			border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
		},
	};
}

function roleLabelFromType(t: ParticipantType): "Assinante" | "Testemunha" | "Observador" {
	if (t === "signer") return "Assinante";
	if (t === "witness") return "Testemunha";
	return "Observador";
}

export default function CardSigner({
	name,
	email,
	documentMasked,
	type,
	status,
	onResendLink,
	onRemove,
	dense = false,
	canResend = true,
	canRemove = true,
	menuDisabled,
}: CardSignerProps) {
	const theme = useTheme();
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const initials =
		(name || "")
			.trim()
			.split(/\s+/)
			.slice(0, 2)
			.map((n) => n[0]?.toUpperCase())
			.join("") || "??";

	const signs = isSignerType(type);
	const visual: VisualResolved = signs ? resolveVisualForWhoSigns(theme, status) : resolveNeutral(theme);

	// rótulo derivado 1:1 do tipo
	const derivedRoleLabel = roleLabelFromType(type);

	// se está assinado, desabilita todo o menu para tipos que assinam
	const disableAllMenu = menuDisabled ?? (signs && norm(status) === "signed");

	return (
		<Box
			sx={{
				position: "relative",
				borderRadius: 2,
				border: "1px solid",
				borderColor: visual.cardBorder,
				bgcolor: visual.cardBg,
				boxShadow: `0 1px 4px ${alpha(theme.palette.common.black, 0.08)}`,
				transition: "transform .12s ease, box-shadow .12s ease",
				"&:hover": {
					transform: "translateY(-2px)",
					boxShadow: `0 6px 14px ${alpha(theme.palette.common.black, 0.12)}`,
				},
				"&::before": {
					content: '""',
					position: "absolute",
					left: 0,
					top: 0,
					bottom: 0,
					width: 4,
					borderTopLeftRadius: 8,
					borderBottomLeftRadius: 8,
					bgcolor: visual.accent,
				},
				p: dense ? 1.25 : 1.75,
			}}
		>
			<Stack direction="row" spacing={1.5} alignItems="center">
				<Avatar
					sx={{
						width: dense ? 34 : 40,
						height: dense ? 34 : 40,
						fontSize: dense ? 14 : 16,
						background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.secondary.main, 0.9)} 100%)`,
					}}
				>
					{initials}
				</Avatar>

				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Stack direction="row" alignItems="center" spacing={1}>
						<Tooltip title={name}>
							<Typography fontWeight={700} noWrap>
								{name}
							</Typography>
						</Tooltip>

						<Chip
							size="small"
							label={derivedRoleLabel}
							sx={{
								height: 22,
								borderRadius: 1,
								bgcolor: alpha(theme.palette.grey[500], 0.12),
								border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`,
							}}
						/>

						{signs && <Chip size="small" icon={<visual.ChipIcon />} label={visual.chipLabel} sx={visual.chipSx} />}
					</Stack>

					<Typography variant="body2" color="text.secondary" noWrap title={`${documentMasked} • ${email}`} sx={{ mt: dense ? 0.25 : 0.5 }}>
						{documentMasked} • {email}
					</Typography>
				</Box>

				<span>
					<IconButton size="small" disabled={disableAllMenu} onClick={(e) => setAnchorEl(e.currentTarget)}>
						<MoreVertIcon fontSize="small" />
					</IconButton>
				</span>

				<Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
					{signs && canResend && (
						<MenuItem
							onClick={() => {
								setAnchorEl(null);
								onResendLink?.();
							}}
						>
							<ListItemIcon>
								<ReplayIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText>Reenviar link</ListItemText>
						</MenuItem>
					)}
					{canRemove && (
						<MenuItem
							onClick={() => {
								setAnchorEl(null);
								onRemove?.();
							}}
						>
							<ListItemIcon>
								<DeleteOutlineIcon fontSize="small" />
							</ListItemIcon>
							<ListItemText>Remover / cancelar</ListItemText>
						</MenuItem>
					)}
				</Menu>
			</Stack>
		</Box>
	);
}
